import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Info, TriangleAlert, X, XCircle } from 'lucide-react';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

/** Akcja opcjonalna renderowana jako przycisk w toaście (np. "Cofnij" po odłączeniu
 * powiązania — patrz `useRecordLinkMutations` w `hooks/useRecordLinks.ts`). Klik
 * wywołuje `onClick` i zamyka toast — akcja NIE resetuje autohide timera samodzielnie. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  title?: string;
  description?: string;
  variant?: ToastVariant;
  duration?: number; // ms; 0 = nie autohide
  action?: ToastAction;
}

interface ToastEntry extends Required<Omit<ToastOptions, 'title' | 'description' | 'action'>> {
  id: string;
  title?: string;
  description?: string;
  action?: ToastAction;
}

export interface ToastContextValue {
  show: (opts: ToastOptions) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION = 5000;

const VARIANT_ICON = {
  success: CheckCircle2,
  error: XCircle,
  warning: TriangleAlert,
  info: Info,
} as const;

// Ton niesie WYŁĄCZNIE ramka + kolor ikony (tokeny semantyczne „08.7 · Feedback"). Tło
// (`bg-bg-elevated`) i tekst (text-primary/secondary) są neutralne/theme-aware, więc toast
// czyta się w light i dark bez osobnych nadpisań — kolor tonu to sygnał, nie tło.
const VARIANT_CLASSES: Record<ToastVariant, string> = {
  success: 'border-[rgb(var(--color-success))]',
  error: 'border-[rgb(var(--color-error))]',
  warning: 'border-[rgb(var(--color-warning))]',
  info: 'border-[rgb(var(--color-info))]',
};

const VARIANT_ICON_COLOR: Record<ToastVariant, string> = {
  success: 'text-[rgb(var(--color-success))]',
  error: 'text-[rgb(var(--color-error))]',
  warning: 'text-[rgb(var(--color-warning))]',
  info: 'text-[rgb(var(--color-info))]',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const mountedRef = useRef(true);

  // Sprząta timery auto-dismiss przy odmontowaniu providera — bez tego timer
  // zaplanowany przez `show()` (np. 8s toast "Cofnij") odpala się PO odmontowaniu
  // (np. po teardownie testu) i wywołuje setState na martwym komponencie.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current.clear();
    };
  }, []);

  const dismiss = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    // Defense in depth: gdyby timer mimo czyszczenia w unmount-efekcie zdążył
    // już odpalić swój callback, nie dotykaj stanu odmontowanego providera.
    if (!mountedRef.current) return;
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (opts: ToastOptions): string => {
      const id = `toast_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const entry: ToastEntry = {
        id,
        title: opts.title,
        description: opts.description,
        variant: opts.variant ?? 'info',
        duration: opts.duration ?? DEFAULT_DURATION,
        action: opts.action,
      };
      setToasts((prev) => [...prev, entry]);
      if (entry.duration > 0) {
        const timer = setTimeout(() => dismiss(id), entry.duration);
        timersRef.current.set(id, timer);
      }
      return id;
    },
    [dismiss],
  );

  const clear = useCallback(() => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current.clear();
    setToasts([]);
  }, []);

  const value = useMemo(() => ({ show, dismiss, clear }), [show, dismiss, clear]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(<ToastViewport toasts={toasts} onDismiss={dismiss} />, document.body)}
    </ToastContext.Provider>
  );
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastEntry[];
  onDismiss: (id: string) => void;
}) {
  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-[60] flex flex-col gap-2"
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((t) => {
        const Icon = VARIANT_ICON[t.variant];
        return (
          <div
            key={t.id}
            role={t.variant === 'error' ? 'alert' : 'status'}
            data-testid="toast"
            data-variant={t.variant}
            className={[
              // Cień = Elevation/Medium z systemu (dwuwarstwowy, miękki) zamiast tailwindowego shadow-xl.
              'pointer-events-auto flex w-80 items-start gap-3 rounded-xl border bg-bg-elevated p-3',
              'shadow-[0_2px_6px_-1px_rgba(23,23,33,0.07),0_1px_2px_rgba(23,23,33,0.04)]',
              VARIANT_CLASSES[t.variant],
            ].join(' ')}
          >
            <Icon
              size={20}
              aria-hidden="true"
              className={['mt-0.5 flex-shrink-0', VARIANT_ICON_COLOR[t.variant]].join(' ')}
            />
            <div className="flex-1 min-w-0">
              {t.title && <div className="text-sm font-medium text-text-primary">{t.title}</div>}
              {t.description && (
                <div className="mt-0.5 text-[13px] leading-5 text-text-secondary">
                  {t.description}
                </div>
              )}
              {t.action && (
                <button
                  type="button"
                  onClick={() => {
                    t.action?.onClick();
                    onDismiss(t.id);
                  }}
                  className="mt-1 text-xs font-semibold underline decoration-2 underline-offset-2 hover:opacity-80"
                >
                  {t.action.label}
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => onDismiss(t.id)}
              aria-label="Zamknij powiadomienie"
              className="flex-shrink-0 rounded p-0.5 text-text-muted hover:bg-bg-tertiary hover:text-text-primary transition-colors"
            >
              <X size={14} aria-hidden="true" />
            </button>
          </div>
        );
      })}
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

/**
 * Wariant null-safe — dla hooków współdzielonych (np. `useTableLayout`), których
 * konsumenci nie zawsze siedzą pod ToastProviderem (testy stron z ConfigurableTable).
 * Brak providera = brak toastu, zero wyjątku.
 */
export function useOptionalToast(): ToastContextValue | null {
  return useContext(ToastContext);
}
